import { useState, useContext, useMemo } from "react";
import { trpc } from "../lib/trpc";
import { I18nContext } from "../i18n";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, GripVertical } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { DEFAULT_CURRENCY } from "@shared/const";

type PrintFormat = {
  id: number;
  photographerId: number;
  name: string;
  width: number;
  height: number;
  unit: string;
  price: string;
  isActive: boolean;
  sortOrder: number;
};

export function PrintFormatsManager() {
  const { t } = useContext(I18nContext);
  const { data: formats, refetch } = trpc.printFormats.list.useQuery();
  const { data: settings } = trpc.settings.get.useQuery();
  const currency = settings?.currency || DEFAULT_CURRENCY;
  const formatCurrency = useMemo(
    () => (value: string) => `${parseFloat(value || "0").toFixed(2)} ${currency}`,
    [currency]
  );
  const priceLabel = (t.printFormats.price || "Price ({currency})").replace("{currency}", currency);
  const [editingFormat, setEditingFormat] = useState<PrintFormat | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    width: 10,
    height: 15,
    unit: "cm" as "cm" | "mm",
    price: "5.00",
    isActive: true,
    sortOrder: 0,
  });

  const createMutation = trpc.printFormats.create.useMutation({
    onSuccess: () => {
      toast.success(t.printFormats.created);
      refetch();
      setIsCreating(false);
      resetForm();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const updateMutation = trpc.printFormats.update.useMutation({
    onSuccess: () => {
      toast.success(t.printFormats.updated);
      refetch();
      setEditingFormat(null);
      resetForm();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const deleteMutation = trpc.printFormats.delete.useMutation({
    onSuccess: () => {
      toast.success(t.printFormats.deleted);
      refetch();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const resetForm = () => {
    setFormData({
      name: "",
      width: 10,
      height: 15,
      unit: "cm",
      price: "5.00",
      isActive: true,
      sortOrder: 0,
    });
  };

  const handleEdit = (format: PrintFormat) => {
    setEditingFormat(format);
    setFormData({
      name: format.name,
      width: format.width,
      height: format.height,
      unit: format.unit as "cm" | "mm",
      price: format.price,
      isActive: format.isActive,
      sortOrder: format.sortOrder,
    });
  };

  const handleCreate = () => {
    setIsCreating(true);
    resetForm();
  };

  const handleSave = () => {
    if (editingFormat) {
      updateMutation.mutate({
        id: editingFormat.id,
        ...formData,
      });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (id: number) => {
    if (confirm(t.printFormats.deleteConfirm)) {
      deleteMutation.mutate({ id });
    }
  };

  const handleToggleActive = (format: PrintFormat) => {
    updateMutation.mutate({
      id: format.id,
      isActive: !format.isActive,
    });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{t.printFormats.title}</CardTitle>
          <Button onClick={handleCreate} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            {t.printFormats.newFormat}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {formats && formats.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {t.printFormats.noFormatsMessage}
            </p>
          )}
          {formats?.map((format) => (
            <div
              key={format.id}
              className="flex items-center gap-3 p-3 border rounded-lg hover:bg-accent/50 transition-colors"
            >
              <div className="flex-shrink-0">
                <GripVertical className="h-5 w-5 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{format.name}</span>
                  <span className="text-sm text-muted-foreground">
                    ({format.width}×{format.height} {format.unit})
                  </span>
                </div>
                <div className="text-sm text-muted-foreground">
                  {formatCurrency(format.price)}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={format.isActive}
                  onCheckedChange={() => handleToggleActive(format)}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleEdit(format)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(format.id)}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Create/Edit Dialog */}
        <Dialog
          open={isCreating || editingFormat !== null}
          onOpenChange={(open) => {
            if (!open) {
              setIsCreating(false);
              setEditingFormat(null);
              resetForm();
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingFormat ? t.printFormats.editFormat : t.printFormats.newFormat}
              </DialogTitle>
              <DialogDescription>
                {t.printFormats.dialogDescription}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t.printFormats.name}</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder={t.printFormats.namePlaceholder}
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="width">{t.printFormats.width}</Label>
                  <Input
                    id="width"
                    type="number"
                    value={formData.width}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        width: parseInt(e.target.value) || 0,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="height">{t.printFormats.height}</Label>
                  <Input
                    id="height"
                    type="number"
                    value={formData.height}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        height: parseInt(e.target.value) || 0,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="unit">{t.printFormats.unit}</Label>
                  <Select
                    value={formData.unit}
                    onValueChange={(value: "cm" | "mm") =>
                      setFormData({ ...formData, unit: value })
                    }
                  >
                    <SelectTrigger id="unit">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cm">cm</SelectItem>
                      <SelectItem value="mm">mm</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="price">{priceLabel}</Label>
                <Input
                  id="price"
                  value={formData.price}
                  onChange={(e) =>
                    setFormData({ ...formData, price: e.target.value })
                  }
                  placeholder="5.00"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, isActive: checked })
                  }
                />
                <Label htmlFor="isActive">{t.printFormats.active}</Label>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsCreating(false);
                  setEditingFormat(null);
                  resetForm();
                }}
              >
                {t.printFormats.cancel}
              </Button>
              <Button onClick={handleSave}>{t.printFormats.save}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
